import React, {useContext, useEffect, useState} from "react";
import "./AppDomainOverview.css";
import {FormattedMessage, useIntl} from "react-intl";
import {
  Icon,
  IconButton,
  ModalContext,
  TableColumn,
} from "@spg/component_library";
import {SnackbarContext} from "../../reusables/SnackbarProvider";
import {ScreenSize} from "../utility/ScreenSize";
import API, {Resolution} from "../../logic/API";
import {ApplicationDomain} from "../../logic/APITypes";
import trashIcon from "../../images/trash.svg";
import edit from "../../images/edit.svg";
import plusIcon from "../../images/plus.svg";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {AppDomainCreation} from "./AppDomainCreation";
import {Table} from "../../reusables/Table";
import {KeycloakContext} from "../utility/KeycloakProvider";

/**
 * A page with a tabular overview of all app domains
 */
function AppDomainOverview() {
  const navigate = useNavigate();
  const location = useLocation();
  const {keycloak, initialized} = useContext(KeycloakContext);
  const addSnackbar = useContext(SnackbarContext).addSnackbar;
  const intl = useIntl();

  const urlAppDomainId = location.pathname.split("/")[3];
  const [isAdmin, setIsAdmin] = useState(false);
  const [appDomains, setAppDomains] = useState<
    Resolution<Array<Omit<ApplicationDomain, "data_streams">>>
  >({
    success: false,
    unresolved: true,
  });
  const [editingAppDomain, setEditingAppDomain] = useState<
    | {
        alternative: "view" | "edit";
        appDomain: Resolution<ApplicationDomain>;
        id: string;
      }
    | {alternative: "create"}
  >();
  const [freshAppDomain, setFreshAppDomain] = useState<ApplicationDomain>();

  const columns: TableColumn[] = [
    {
      key: "name",
      label: <FormattedMessage id="appDomainOverview.name" />,
      sortable: true,
      columnCount: {
        "Big Desktop": 3,
        Desktop: 3,
        Tablet: 2,
        Mobile: isAdmin ? 3 : 4,
      },
    },
    {
      key: "description",
      label: <FormattedMessage id="appDomainOverview.description" />,
      columnCount: {
        "Big Desktop": isAdmin ? 8 : 9,
        Desktop: isAdmin ? 8 : 9,
        Tablet: isAdmin ? 5 : 6,
      },
      collapsibleMobile: true,
    },
  ];

  if (isAdmin) {
    columns.push({
      key: "edit",
      label: <FormattedMessage id="appDomainOverview.edit" />,
      srOnlyLabel: true,
      sortable: false,
      columnCount: {
        "Big Desktop": 1,
        Desktop: 1,
        Tablet: 1,
        Mobile: 1,
      },
    });
  }

  const getAppDomains = async () => {
    setAppDomains(await API.getAppDomains());
  };

  const fetchAndSetEditingAppDomain = (
    alternative: "view" | "edit",
    appDomain: Omit<ApplicationDomain, "data_streams">,
    useNavigation: boolean,
  ) => {
    if (useNavigation) {
      localStorage.setItem("appDomainCreationOriginPage", "domains");
      navigate("/domains/overview/" + appDomain.id);
    }
    setFreshAppDomain(undefined);
    if (freshAppDomain && alternative === "view") {
      setEditingAppDomain({
        alternative,
        id: freshAppDomain.id,
        appDomain: {success: true, payload: freshAppDomain},
      });
    } else {
      setEditingAppDomain({
        alternative,
        id: appDomain.id,
        appDomain: {success: false, unresolved: true},
      });
      API.getAppDomain(appDomain.id).then((response) => {
        if (response.success)
          setEditingAppDomain({
            alternative,
            id: appDomain.id,
            appDomain: response,
          });
        else {
          addSnackbar({
            variant: "close",
            text: intl.formatMessage({
              id: "appDomainOverview.snackbar.view.error.text",
            }),
            hasCloseButton: true,
          });
          if (
            localStorage.getItem("appDomainCreationOriginPage") ===
            "other/applications"
          )
            navigate("/other/applications");
          else {
            setEditingAppDomain(undefined);
            navigate("/domains");
          }
        }
      });
    }
  };

  const deleteAppDomain = (id: string) => {
    if (appDomains.payload) {
      const index = appDomains.payload?.findIndex(
        (element) => element.id === id,
      );
      if (index !== -1) {
        const updatedAppDomains = {...appDomains};
        if (updatedAppDomains.payload)
          updatedAppDomains.payload.splice(index, 1);
        setAppDomains(updatedAppDomains);
      }
    }
  };

  const rows = !appDomains.payload
    ? []
    : appDomains.payload.map((details, index) => ({
        id: details.id,
        cells: {
          name: {
            content: details.name,
            hasToolTip: true,
            link: {
              to: "/domains/overview/" + details.id,
              onClick: () => {
                localStorage.setItem("appDomainCreationOriginPage", "domains");
              },
            },
          },
          description: {
            content: details.description,
            hasToolTip: true,
          },
          edit: isAdmin
            ? {
                sortingKey: "",
                value: (
                  <IconButton
                    idBase={"appDomainOverviewEdit" + details.id}
                    onClick={() =>
                      fetchAndSetEditingAppDomain("edit", details, true)
                    }
                    buttonSize={{height: 48, width: 48}}
                    iconProps={{
                      href: edit + "#globalG",
                      strokeColor: "#FF3D00",
                      title: intl.formatMessage({
                        id: "appDomainOverview.edit",
                      }),
                    }}
                  />
                ),
              }
            : {
                sortingKey: "",
                value: "",
              },
        },
        expandableContent: (
          <ExpandableContent
            index={index}
            content={details}
            onDelete={deleteAppDomain}
            isAdmin={isAdmin}
          />
        ),
      }));

  useEffect(() => {
    getAppDomains();
  }, []);

  useEffect(() => {
    setIsAdmin(keycloak.hasResourceRole("DM Administration"));
  }, [initialized]);

  useEffect(() => {
    if (location.pathname === "/domains/create") {
      setEditingAppDomain({alternative: "create"});
      return;
    }
    if (!urlAppDomainId || !appDomains.payload) {
      setEditingAppDomain(undefined);
      return;
    }
    const appDomain = appDomains.payload.find(
      (desc) => desc.id === urlAppDomainId,
    );
    if (
      appDomain &&
      (!editingAppDomain ||
        (editingAppDomain &&
          "id" in editingAppDomain &&
          appDomain.id !== editingAppDomain?.id))
    )
      fetchAndSetEditingAppDomain("view", appDomain, false);
  }, [location.pathname, appDomains.payload]);

  // Don't show table while fetching the editing app domain after entering from another page
  if (
    (appDomains.unresolved && (editingAppDomain || urlAppDomainId)) ||
    (!editingAppDomain &&
      urlAppDomainId &&
      (appDomains.payload ?? []).some((desc) => desc.id === urlAppDomainId))
  )
    return null;

  // Render the detail- or edit-page instead of the table
  if (appDomains.payload && editingAppDomain) {
    if (editingAppDomain.alternative == "create")
      return (
        <AppDomainCreation
          alternative={"create"}
          onSuccessfulCreation={(domain: ApplicationDomain) => {
            setAppDomains((oldDomains) => ({
              ...oldDomains,
              payload: [...(oldDomains.payload || []), domain],
            }));
            setEditingAppDomain(undefined);
            setFreshAppDomain(domain);
            navigate("/domains/overview/" + domain.id);
          }}
          onCancel={() => {
            navigate("/domains/overview");
            setEditingAppDomain(undefined);
          }}
        />
      );
    const selectedAppDomain = appDomains.payload.find(
      (t) => t.id === editingAppDomain.id,
    );
    if (selectedAppDomain)
      return (
        <AppDomainCreation
          alternative={editingAppDomain.alternative}
          viewEditInfo={{
            onSuccessfulEdit: (id, appDomain) => {
              if (appDomains.payload) {
                const index = appDomains.payload?.findIndex(
                  (element) => element.id === id,
                );
                if (index !== -1) {
                  const updatedAppDomains = {...appDomains};
                  if (updatedAppDomains.payload)
                    updatedAppDomains.payload[index] = {
                      id: id,
                      name: appDomain.name,
                      description: appDomain.description,
                      app_name: appDomain.app_name,
                    };
                  setAppDomains(updatedAppDomains);
                }
                setEditingAppDomain({
                  alternative: "view",
                  id: id,
                  appDomain: {
                    ...editingAppDomain.appDomain,
                    payload: {id: id, ...appDomain},
                  },
                });
              }
            },
            onBack: () => {
              navigate("/domains/overview");
              setEditingAppDomain(undefined);
            },
            initialAppDomain: editingAppDomain.appDomain,
          }}
        />
      );
  }

  return (
    <div className="appDomainOverview landingPageGrid">
      <h1 id="appDomainOverviewHeading">
        <FormattedMessage id="appDomainOverview.title" />
      </h1>
      {keycloak.hasResourceRole("DM Administration") && (
        <Link
          id="createNewDomainLink"
          to={window.location.origin + "/datamarket/domains/create"}
          onClick={() => {
            localStorage.setItem("appDomainCreationOriginPage", "domains");
          }}
          className="appDomainOverviewNew"
        >
          <Icon
            href={plusIcon + "#globalG"}
            title={""}
            iconSize={{width: 36, height: 36}}
          />
          <span>
            <FormattedMessage id="appDomainOverview.new" />
          </span>
        </Link>
      )}
      {rows.length === 0 && !appDomains.unresolved ? (
        <div className="appDomainOverviewBlankText">
          <FormattedMessage
            id={
              "appDomainOverview." + (appDomains.success ? "blank" : "failed")
            }
          />
        </div>
      ) : (
        <Table
          id={"appDomainTable"}
          data={rows}
          columns={columns}
          pageSize={12}
          ariaLabelledBy={"appDomainOverviewHeading"}
          isLoading={appDomains.unresolved}
        />
      )}
    </div>
  );
}

export {AppDomainOverview};

type ExpandableContentProps = {
  /** An index to avoid id collisions */
  index: number;
  /** The relevant content to display */
  content: Omit<ApplicationDomain, "app_name" | "data_streams">;
  /** Function triggered when an application domain is deleted */
  onDelete: (id: string) => void;
  /** Whether the user is an admin */
  isAdmin: boolean;
};

function ExpandableContent(props: ExpandableContentProps) {
  const screenSize = useContext(ScreenSize);
  const modalContext = useContext(ModalContext);
  const addSnackbar = useContext(SnackbarContext).addSnackbar;
  const intl = useIntl();

  const [interactionStatus, setInteractionStatus] = useState<
    "IDLE" | "PENDING"
  >("IDLE");

  const deleteAppDomain = () => {
    setInteractionStatus("PENDING");
    API.deleteAppDomain(props.content.id).then((resolution) => {
      if (resolution.httpCode)
        if (resolution.httpCode === 204) {
          props.onDelete(props.content.id);
          addSnackbar({
            variant: "timeout",
            text: intl.formatMessage({id: "appDomainDeletion.success"}),
            timeout: 7000,
          });
        } else if (resolution.httpCode >= 400) {
          addSnackbar({
            variant: "close",
            text: intl.formatMessage({id: "appDomainDeletion.error"}),
            hasCloseButton: true,
          });
        }
      setInteractionStatus("IDLE");
    });
  };

  return (
    <div className="domainExpandContainer">
      <div className="domainExpandDescription">
        <div className="domainExpandHeader">
          <FormattedMessage id="appDomainDescription.expand.description" />
        </div>
        {props.content.description}
      </div>
      {(screenSize.category === "Desktop" ||
        screenSize.category === "Big Desktop") && (
        <div className="domainExpandDivider"></div>
      )}
      {props.isAdmin && (
        <div className="domainDeletionContainer">
          <IconButton
            idBase={`delete_${props.content.id}_`}
            onClick={() => {
              modalContext.showModal({
                idBase: "appDomainDeletionModal",
                headline: intl.formatMessage({
                  id: "appDomainDeletion.modal.headline",
                }),
                body: (
                  <div className="modalText">
                    <FormattedMessage
                      id="appDomainDeletion.modal.text"
                      values={{
                        br: () => <br />,
                      }}
                    />
                  </div>
                ),
                primaryButton: {
                  name: intl.formatMessage({
                    id: "appDomainDeletion.modal.delete",
                  }),
                  onClick: () => deleteAppDomain(),
                },
                secondaryButton: {
                  name: intl.formatMessage({
                    id: "appDomainDeletion.modal.cancel",
                  }),
                  onClick: () => undefined,
                },
              });
            }}
            hoverColor="var(--semTableColorIconHover)"
            iconProps={{
              href: trashIcon + "#globalG",
              strokeColor: "var(--semIconbtnColorIconDefault)",
              title: intl.formatMessage(
                {id: "appDomainDeletion.a11y"},
                {index: props.content.name},
              ),
            }}
            disabled={interactionStatus === "PENDING"}
          />
        </div>
      )}
    </div>
  );
}
